import classNames from "classnames"
import React, { ReactElement } from "react"
import { useAsyncCallback } from "react-async-hook"

import viewManager from "~/shared/util/viewManager"

import "./PreviewBanner.less"

async function exitPreview(): Promise<void> {
  try {
    await fetch("/_preview/exit", { credentials: "include" })
  } catch {
    // Best-effort cookie deletion — reload regardless so the user
    // sees the current (possibly still-preview) state.
  }
  window.location.reload()
  // Keep the promise pending so useAsyncCallback never flips loading back
  // to false — the button stays in "Exiting…" state until the page unloads.
  await new Promise<void>(() => {})
}

export default function PreviewBanner(): ReactElement | null {
  const previewId = viewManager.get("preview") as string | undefined
  const previewStatus = viewManager.get("previewStatus") as string | undefined
  const previewBranch = viewManager.get("previewBranch")
  const previewCommit = viewManager.get("previewCommit")
  const failed = previewStatus === "failed"
  const asyncExit = useAsyncCallback(exitPreview)

  if (!previewId) {
    return null
  }

  const GITHUB_REPO = "https://github.com/oneschema/oneschema"

  const label = failed ? (
    "Preview failed to load"
  ) : previewBranch || previewCommit ? (
    <>
      {"Preview: "}
      {previewBranch && (
        <a
          href={`${GITHUB_REPO}/tree/${previewBranch}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {previewBranch}
        </a>
      )}
      {previewBranch && previewCommit && " @ "}
      {previewCommit && (
        <a
          href={`${GITHUB_REPO}/commit/${previewCommit}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {previewCommit}
        </a>
      )}
    </>
  ) : (
    "Preview Mode"
  )

  return (
    <div className={classNames("PreviewBanner", { "PreviewBanner--failed": failed })}>
      <span>{label}</span>
      <button
        type="button"
        onClick={asyncExit.execute}
        disabled={asyncExit.loading}
        className="PreviewBanner__link"
      >
        {asyncExit.loading ? "Exiting…" : "Exit Preview"}
      </button>
    </div>
  )
}
